// src/pages/SuperAdmin/RxTemplatesManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Input,
  FormGroup,
  Label,
  Table,
} from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import { showSuccessAlert, showConfirmAlert, showErroeAlert } from 'pages/utils/alertMessages';
import {
  RiMedicineBottleLine,
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiSearchLine,
  RiRefreshLine,
  RiCheckLine,
  RiCloseLine,
  RiCapsuleLine,
  RiFileTextLine,
  RiHospitalLine,
  RiShareForwardLine,
} from 'react-icons/ri';

const DEFAULT_MEDICINES_CATALOG = [
  { name: 'Tab. Augmentin 625mg', dose: '625mg', frequent: '1-0-1', duration: '5 days' },
  { name: 'Tab. Moxikind-CV 625', dose: '625mg', frequent: '1-0-1', duration: '5 days' },
  { name: 'Cap. Novamox 500mg', dose: '500mg', frequent: '1-1-1', duration: '5 days' },
  { name: 'Tab. Flagyl 400mg', dose: '400mg', frequent: '1-0-1', duration: '5 days' },
  { name: 'Tab. Metrogyl 400mg', dose: '400mg', frequent: '1-0-1', duration: '5 days' },
  { name: 'Tab. Zerodol-SP', dose: '1 Tab', frequent: '1-0-1', duration: '3 days' },
  { name: 'Tab. Ketorol-DT 10mg', dose: '10mg', frequent: 'SOS', duration: '3 days' },
  { name: 'Tab. Combiflam', dose: '1 Tab', frequent: '1-0-1', duration: '3 days' },
  { name: 'Tab. Dolo 650mg', dose: '650mg', frequent: '1-0-1', duration: '3 days' },
  { name: 'Cap. Pantocid 40mg', dose: '40mg', frequent: '1-0-0', duration: '5 days' },
  { name: 'Cap. Pan-D', dose: '1 Cap', frequent: '1-0-0', duration: '5 days' },
  { name: 'Hexidine Mouthwash 0.2%', dose: '10ml', frequent: '1-0-1', duration: '14 days' },
  { name: 'Betadine 2% Mint Gargle', dose: '10ml', frequent: '1-0-1', duration: '7 days' },
  { name: 'Mucopain Gel', dose: 'Pea Size', frequent: '1-1-1', duration: '5 days' },
  { name: 'Dologel-CT Oral Gel', dose: 'Local', frequent: '1-1-1', duration: '5 days' },
  { name: 'HiOra-GA Gel', dose: 'Local', frequent: '1-0-1', duration: '14 days' },
  { name: 'Tab. Becosules-Z', dose: '1 Cap', frequent: '0-1-0', duration: '15 days' },
  { name: 'Tab. Limcee 500mg', dose: '500mg', frequent: '0-1-0', duration: '15 days' },
  { name: 'Syp. Augmentin DDS', dose: '5ml', frequent: '1-0-1', duration: '5 days' },
  { name: 'Syp. Meftal-P', dose: '5ml', frequent: 'SOS', duration: '3 days' },
];

export const getSafeString = (val, defaultVal = '') => {
  if (val === null || val === undefined) return defaultVal;
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    return val.label || val.name || val.medicine || val.medicine_name || val.title || val.value || defaultVal;
  }
  return defaultVal;
};

export const normalizeMedicinesList = (rawMedicins) => {
  let list = [];
  if (typeof rawMedicins === 'string') {
    try {
      list = JSON.parse(rawMedicins);
    } catch {
      list = [];
    }
  } else if (Array.isArray(rawMedicins)) {
    list = rawMedicins;
  } else if (rawMedicins && typeof rawMedicins === 'object') {
    list = [rawMedicins];
  }

  if (!Array.isArray(list)) return [];

  return list.map((m) => {
    if (!m) return { medicine: '', dose: '1 Tab', frequent: '1-0-1', duration: '5 days' };
    if (typeof m === 'string') {
      return { medicine: m, dose: '1 Tab', frequent: '1-0-1', duration: '5 days' };
    }

    const medName = getSafeString(m.medicine) || getSafeString(m.name) || getSafeString(m.label) || getSafeString(m.value) || 'Medicine';
    const doseVal = getSafeString(m.dose) || getSafeString(m.dosage) || '1 Tab';
    const freqVal = getSafeString(m.frequent) || getSafeString(m.frequency) || '1-0-1';
    const durVal = getSafeString(m.duration) || '5 days';

    return {
      medicine: medName,
      dose: doseVal,
      frequent: freqVal,
      duration: durVal,
    };
  });
};

const RxTemplatesManager = ({ tenants = [] }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    clinic_id: '',
    note: '',
    medicines: [{ medicine: '', dose: '1 Tab', frequent: '1-0-1', duration: '5 days' }],
  });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('search', search);
      if (selectedClinic && selectedClinic !== 'all') query.append('clinic_id', selectedClinic);

      const res = await get(`/superadmin/rx-templates?${query.toString()}`);
      const data = res?.body || res?.data || res || [];
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch Rx templates:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [selectedClinic]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTemplates();
  };

  const handleOpenModal = (tmpl = null) => {
    if (tmpl) {
      setEditingTemplate(tmpl);
      const parsedMeds = normalizeMedicinesList(tmpl.medicins);

      setFormData({
        title: getSafeString(tmpl.title),
        clinic_id: typeof tmpl.clinic_id === 'object' ? (tmpl.clinic_id?.id || tmpl.clinic_id?.value || '') : (tmpl.clinic_id || ''),
        note: getSafeString(tmpl.note),
        medicines: parsedMeds.length > 0 ? parsedMeds : [{ medicine: '', dose: '1 Tab', frequent: '1-0-1', duration: '5 days' }],
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        title: '',
        clinic_id: tenants[0]?.id || '',
        note: '',
        medicines: [{ medicine: '', dose: '1 Tab', frequent: '1-0-1', duration: '5 days' }],
      });
    }
    setModalOpen(true);
  };

  const handleAddMedicineRow = () => {
    setFormData((prev) => ({
      ...prev,
      medicines: [...prev.medicines, { medicine: '', dose: '1 Tab', frequent: '1-0-1', duration: '5 days' }],
    }));
  };

  const handleRemoveMedicineRow = (idx) => {
    setFormData((prev) => ({
      ...prev,
      medicines: prev.medicines.filter((_, i) => i !== idx),
    }));
  };

  const handleMedicineChange = (idx, field, val) => {
    setFormData((prev) => {
      const updated = [...prev.medicines];
      updated[idx] = { ...updated[idx], [field]: getSafeString(val) };
      return { ...prev, medicines: updated };
    });
  };

  const handlePresetSelect = (idx, medObj) => {
    setFormData((prev) => {
      const updated = [...prev.medicines];
      updated[idx] = {
        ...updated[idx],
        medicine: getSafeString(medObj.name || medObj.medicine || medObj.label),
        dose: getSafeString(medObj.dose, '1 Tab'),
        frequent: getSafeString(medObj.frequent, '1-0-1'),
        duration: getSafeString(medObj.duration, '5 days'),
      };
      return { ...prev, medicines: updated };
    });
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showErroeAlert('Please provide a Template Title');
      return;
    }

    const payload = {
      title: getSafeString(formData.title).trim(),
      clinic_id: formData.clinic_id || (tenants[0]?.id || 1),
      note: getSafeString(formData.note),
      medicins: formData.medicines
        .filter((m) => getSafeString(m.medicine).trim() !== '')
        .map((m) => ({
          medicine: getSafeString(m.medicine).trim(),
          dose: getSafeString(m.dose, '1 Tab').trim(),
          frequent: getSafeString(m.frequent, '1-0-1').trim(),
          duration: getSafeString(m.duration, '5 days').trim(),
        })),
    };

    try {
      if (editingTemplate) {
        await put(`/superadmin/rx-templates/${editingTemplate.id}`, payload);
        showSuccessAlert('Rx Template updated successfully!');
      } else {
        await post('/superadmin/rx-templates', payload);
        showSuccessAlert('Rx Template created successfully!');
      }
      setModalOpen(false);
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showErroeAlert('Failed to save Rx template');
    }
  };

  const handleDeleteTemplate = async (tmpl) => {
    const titleText = getSafeString(tmpl.title, 'Rx Template');
    const { isConfirmed } = await showConfirmAlert(
      'Delete Master Rx Template',
      `Are you sure you want to permanently delete "${titleText}"?`,
      'Yes, Delete',
      'Deleted!',
      'Rx Template removed successfully.'
    );

    if (isConfirmed) {
      try {
        await del(`/superadmin/rx-templates/${tmpl.id}`);
        fetchTemplates();
      } catch (err) {
        showErroeAlert('Failed to delete Rx template');
      }
    }
  };

  const handleSeedAllClinics = async () => {
    if (!window.confirm('Push and seed standard dental formulary & clinical Rx templates across all clinics?')) return;
    try {
      setLoading(true);
      const res = await post('/superadmin/seed-rx-templates', {});
      if (res?.success) {
        showSuccessAlert('Standard Rx templates and medicines pushed to all clinics!');
        fetchTemplates();
      }
    } catch (err) {
      showErroeAlert('Failed to seed Rx templates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rx-templates-manager-container py-2">
      {/* Action Header Card */}
      <Card className="border shadow-2xs mb-3">
        <CardBody className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="font-size-15 font-weight-bold text-dark mb-0">
                  <RiMedicineBottleLine className="text-primary me-1" />
                  Master Prescription Templates & Clinical Protocols
                </h5>
                <Badge color="primary" pill className="font-size-11 px-2 py-1">
                  {templates.length} Templates
                </Badge>
              </div>
              <p className="text-muted font-size-12 mb-0 mt-0.5">
                Configure standardized dental pharmacopeia templates and push verified drug protocols across multi-tenant clinics.
              </p>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                color="warning"
                size="sm"
                className="font-size-12 d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs text-dark"
                onClick={handleSeedAllClinics}
                disabled={loading}
              >
                <RiShareForwardLine /> Push Standard Rx to All Clinics
              </Button>

              <Button
                color="primary"
                size="sm"
                className="font-size-12 d-inline-flex align-items-center gap-1 font-weight-semibold shadow-2xs"
                onClick={() => handleOpenModal()}
              >
                <RiAddLine /> + New Master Rx Template
              </Button>
            </div>
          </div>

          <hr className="my-2.5 opacity-25" />

          {/* Search and Filters Bar */}
          <form onSubmit={handleSearchSubmit} className="row g-2 align-items-center">
            <Col md={5} sm={12}>
              <div className="position-relative">
                <Input
                  type="text"
                  className="form-control form-control-sm font-size-12 ps-4"
                  placeholder="Search template title, drug name, or instructions..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <RiSearchLine className="position-absolute text-muted" style={{ top: '8px', left: '10px' }} />
              </div>
            </Col>

            <Col md={4} sm={6}>
              <Input
                type="select"
                className="form-select form-select-sm font-size-12"
                value={selectedClinic}
                onChange={(e) => setSelectedClinic(e.target.value)}
              >
                <option value="all">All Clinics / Global Scope</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    Clinic #{t.id} - {t.clinic_name} ({t.city || 'India'})
                  </option>
                ))}
              </Input>
            </Col>

            <Col md={3} sm={6} className="d-flex gap-1.5">
              <Button type="submit" color="dark" size="sm" className="font-size-12 px-3">
                <RiSearchLine className="me-1" /> Filter
              </Button>
              <Button
                type="button"
                color="light"
                size="sm"
                className="font-size-12"
                onClick={() => {
                  setSearch('');
                  setSelectedClinic('all');
                  fetchTemplates();
                }}
              >
                <RiRefreshLine />
              </Button>
            </Col>
          </form>
        </CardBody>
      </Card>

      {/* Templates Cards Grid */}
      <Row className="g-3">
        {templates.map((tmpl) => {
          const meds = normalizeMedicinesList(tmpl.medicins);
          const titleText = getSafeString(tmpl.title, 'Prescription Protocol');
          const clinicText = getSafeString(tmpl.clinic?.clinic_name) || `Clinic #${getSafeString(tmpl.clinic_id, 'Global')}`;
          const noteText = getSafeString(tmpl.note);

          return (
            <Col lg={6} md={12} key={tmpl.id}>
              <Card className="h-100 border shadow-2xs hover-shadow transition-all">
                <CardBody className="p-3 d-flex flex-column justify-content-between">
                  <div>
                    {/* Header: Title & Clinic Scope */}
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <h6 className="font-size-14 font-weight-bold text-dark mb-0.5 d-flex align-items-center gap-1.5">
                          <RiCapsuleLine className="text-primary font-size-16" />
                          {titleText}
                        </h6>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-light text-secondary border font-size-10 font-weight-semibold">
                            <RiHospitalLine className="me-1" />
                            {clinicText}
                          </span>
                          <span className="badge bg-soft-success text-success font-size-10 font-weight-bold">
                            {meds.length} Prescribed Drugs
                          </span>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-1">
                        <Button
                          color="light"
                          size="sm"
                          className="btn-icon p-1 text-primary border"
                          style={{ width: '28px', height: '28px' }}
                          onClick={() => handleOpenModal(tmpl)}
                          title="Edit Template"
                        >
                          <RiEditLine />
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          className="btn-icon p-1 text-danger border"
                          style={{ width: '28px', height: '28px' }}
                          onClick={() => handleDeleteTemplate(tmpl)}
                          title="Delete Template"
                        >
                          <RiDeleteBinLine />
                        </Button>
                      </div>
                    </div>

                    {/* Medicines List Table */}
                    <div className="table-responsive rounded border bg-light my-2">
                      <Table className="table-sm table-borderless mb-0 font-size-11">
                        <thead>
                          <tr className="bg-white border-bottom text-muted font-weight-bold">
                            <th className="py-1 px-2">#</th>
                            <th className="py-1 px-2">Medicine / Drug</th>
                            <th className="py-1 px-2 text-center">Dosage</th>
                            <th className="py-1 px-2 text-center">Frequency</th>
                            <th className="py-1 px-2 text-end">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(meds) && meds.length > 0 ? (
                            meds.map((m, idx) => (
                              <tr key={idx} className="border-bottom border-light">
                                <td className="py-1 px-2 text-muted font-weight-bold">{idx + 1}</td>
                                <td className="py-1 px-2 font-weight-semibold text-dark">
                                  {getSafeString(m.medicine || m.name || m.label, 'Medicine')}
                                </td>
                                <td className="py-1 px-2 text-center">
                                  <span className="badge bg-white text-dark border font-size-10">
                                    {getSafeString(m.dose, '1 Tab')}
                                  </span>
                                </td>
                                <td className="py-1 px-2 text-center">
                                  <span className="badge bg-soft-primary text-primary font-size-10 font-weight-bold">
                                    {getSafeString(m.frequent, '1-0-1')}
                                  </span>
                                </td>
                                <td className="py-1 px-2 text-end text-muted font-size-10">{getSafeString(m.duration, '5 days')}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="py-2 text-center text-muted font-size-11">
                                No medicines configured in this template.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Patient Clinical Instructions / Note */}
                    {noteText && (
                      <div className="bg-white p-2 rounded border font-size-11 text-muted d-flex align-items-start gap-1.5 mt-2">
                        <RiFileTextLine className="text-warning font-size-14 mt-0.5 flex-shrink-0" />
                        <div>
                          <strong className="text-dark">Patient Directions: </strong>
                          {noteText}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}

        {templates.length === 0 && !loading && (
          <Col md={12}>
            <div className="text-center py-5 bg-light rounded border">
              <RiMedicineBottleLine className="display-4 text-muted mb-2 opacity-50" />
              <h5 className="font-size-14 text-dark font-weight-bold">No Rx Templates Found</h5>
              <p className="text-muted font-size-12 mb-3">
                Click below to seed the standard clinical pharmacopeia or create a custom master protocol.
              </p>
              <Button color="primary" size="sm" onClick={handleSeedAllClinics}>
                <RiShareForwardLine className="me-1" /> Seed Standard Dental Formulary & Rx
              </Button>
            </div>
          </Col>
        )}
      </Row>

      {/* Create / Edit Master Rx Template Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <form onSubmit={handleSaveTemplate}>
          <ModalHeader toggle={() => setModalOpen(false)} className="bg-light py-2.5 px-3">
            <div className="d-flex align-items-center gap-1.5 font-size-14 font-weight-bold text-dark">
              <RiMedicineBottleLine className="text-primary font-size-16" />
              {editingTemplate ? 'Edit Master Rx Template' : 'Create Master Rx Template'}
            </div>
          </ModalHeader>

          <ModalBody className="p-3">
            <Row className="g-3 mb-3">
              <Col md={8} sm={12}>
                <FormGroup className="mb-0">
                  <Label className="font-size-12 font-weight-semibold mb-1">
                    Protocol Title * (e.g. Root Canal & Apical Abscess Course)
                  </Label>
                  <Input
                    type="text"
                    className="form-control-sm font-size-12"
                    placeholder="Enter template title..."
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </FormGroup>
              </Col>

              <Col md={4} sm={12}>
                <FormGroup className="mb-0">
                  <Label className="font-size-12 font-weight-semibold mb-1">Target Clinic / Scope</Label>
                  <Input
                    type="select"
                    className="form-select-sm font-size-12"
                    value={formData.clinic_id}
                    onChange={(e) => setFormData({ ...formData, clinic_id: e.target.value })}
                  >
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        Clinic #{t.id} - {t.clinic_name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {/* Medicines List Builder */}
            <div className="rounded border bg-light p-2.5 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Label className="font-size-12 font-weight-bold text-dark mb-0">
                  Prescribed Medicines ({formData.medicines.length})
                </Label>
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="font-size-11 py-1 px-2 d-inline-flex align-items-center gap-1"
                  onClick={handleAddMedicineRow}
                >
                  <RiAddLine /> Add Drug Row
                </Button>
              </div>

              <div className="d-flex flex-column gap-2" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {formData.medicines.map((m, idx) => (
                  <div key={idx} className="bg-white p-2 rounded border shadow-2xs">
                    <Row className="g-2 align-items-center">
                      <Col md={4} sm={12}>
                        <div className="d-flex align-items-center gap-1">
                          <span className="badge bg-light text-muted font-size-10 border">#{idx + 1}</span>
                          <Input
                            type="text"
                            className="form-control-sm font-size-12 flex-grow-1"
                            placeholder="Medicine Name (e.g. Augmentin 625mg)"
                            value={m.medicine}
                            onChange={(e) => handleMedicineChange(idx, 'medicine', e.target.value)}
                            required
                          />
                        </div>
                      </Col>

                      <Col md={2} sm={4}>
                        <Input
                          type="text"
                          className="form-control-sm font-size-11"
                          placeholder="Dosage (1 Tab)"
                          value={m.dose}
                          onChange={(e) => handleMedicineChange(idx, 'dose', e.target.value)}
                        />
                      </Col>

                      <Col md={2} sm={4}>
                        <Input
                          type="select"
                          className="form-select-sm font-size-11"
                          value={m.frequent}
                          onChange={(e) => handleMedicineChange(idx, 'frequent', e.target.value)}
                        >
                          <option value="1-0-1">1-0-1 (BD)</option>
                          <option value="1-1-1">1-1-1 (TDS)</option>
                          <option value="1-0-0">1-0-0 (OD - Morning)</option>
                          <option value="0-0-1">0-0-1 (OD - Night)</option>
                          <option value="0-1-0">0-1-0 (Afternoon)</option>
                          <option value="SOS">SOS (When Needed)</option>
                          <option value="STAT">STAT (Immediately)</option>
                        </Input>
                      </Col>

                      <Col md={3} sm={4}>
                        <Input
                          type="text"
                          className="form-control-sm font-size-11"
                          placeholder="Duration (5 days)"
                          value={m.duration}
                          onChange={(e) => handleMedicineChange(idx, 'duration', e.target.value)}
                        />
                      </Col>

                      <Col md={1} sm={12} className="text-end">
                        <Button
                          type="button"
                          color="outline-danger"
                          size="sm"
                          className="p-1 btn-icon"
                          style={{ width: '28px', height: '28px' }}
                          onClick={() => handleRemoveMedicineRow(idx)}
                          disabled={formData.medicines.length === 1}
                        >
                          <RiDeleteBinLine />
                        </Button>
                      </Col>
                    </Row>

                    {/* Quick Suggestions Pills */}
                    <div className="d-flex align-items-center gap-1 mt-1.5 flex-wrap">
                      <span className="text-muted font-size-10">Quick Drug:</span>
                      {DEFAULT_MEDICINES_CATALOG.slice(0, 5).map((preset, pIdx) => (
                        <button
                          key={pIdx}
                          type="button"
                          className="btn btn-light btn-xs p-0 px-1 font-size-10 border text-muted"
                          onClick={() => handlePresetSelect(idx, preset)}
                        >
                          + {preset.name.split(' ')[1] || preset.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Patient Clinical Instructions / Note */}
            <FormGroup className="mb-0">
              <Label className="font-size-12 font-weight-semibold mb-1">
                Patient Instructions / Clinical Note
              </Label>
              <Input
                type="textarea"
                rows={3}
                className="font-size-12"
                placeholder="e.g. Take all medications strictly after food. Complete the full 5-day course. Rinse with Hexidine mouthwash twice daily."
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              />
            </FormGroup>
          </ModalBody>

          <ModalFooter className="bg-light py-2 px-3">
            <Button color="light" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" size="sm" type="submit">
              {editingTemplate ? 'Update Master Protocol' : 'Save Master Protocol'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default RxTemplatesManager;
